import { motion } from "framer-motion";
import { INSIGHTS } from "@/lib/content";
import { fadeUp, stagger } from "@/lib/motion";
import { ExternalLink, ArrowUpRight } from "lucide-react";

export default function Insights() {
  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="relative bg-ink-950 py-28 md:py-40 border-t border-ivory/[0.06]"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          className="mb-16 md:mb-20 flex flex-col md:flex-row items-start md:items-end justify-between gap-6"
        >
          <div>
            <div className="font-mono-caps text-[10px] text-copper mb-6">
              <span className="mr-3">§ 08</span> Media & Insights
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ivory">
              Words,<br />
              <span className="italic text-copper">and quiet observations.</span>
            </h2>
          </div>
          <p className="max-w-sm text-ivory/60 leading-relaxed">
            Long-form features and forthcoming essays on Mumbai real estate, architecture,
            development and AI in the property business.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
        >
          {INSIGHTS.map((p, i) => {
            const isSoon = p.date.startsWith("Coming");
            return (
              <motion.a
                key={i}
                variants={fadeUp}
                href={p.href}
                target={p.external ? "_blank" : "_self"}
                rel={p.external ? "noopener noreferrer" : undefined}
                data-testid={`insight-card-${i}`}
                className="group relative border border-ivory/[0.08] hover:border-copper/60 bg-ink-900/30 transition-colors duration-500 overflow-hidden"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={p.image}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover grayscale group-hover:grayscale-0 scale-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-ink-950/50 group-hover:bg-ink-950/25 transition-colors duration-500" />
                  <div className="absolute top-4 left-4 font-mono-caps text-[9px] bg-ink-950/70 backdrop-blur text-ivory px-2 py-1">
                    {p.tag}
                  </div>
                  {isSoon && (
                    <div className="absolute top-4 right-4 font-mono-caps text-[9px] bg-copper/90 text-ink-950 px-2 py-1">
                      Coming
                    </div>
                  )}
                </div>
                <div className="p-6 md:p-8">
                  <div className="font-mono-caps text-[9px] text-copper mb-3">{p.date}</div>
                  <h3 className="font-serif text-xl md:text-2xl lg:text-3xl text-ivory leading-tight group-hover:text-copper transition-colors duration-300">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-ivory/55 text-sm leading-relaxed">{p.excerpt}</p>
                  <div className="mt-6 flex items-center gap-2 font-mono-caps text-[10px] text-ivory/60 group-hover:text-copper transition-colors">
                    {p.external ? "Read on original source" : "Coming soon"}
                    {p.external ? <ExternalLink size={12} /> : <ArrowUpRight size={12} />}
                  </div>
                </div>
              </motion.a>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
